import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

export const ComplianceOverviewPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['compliance-overview'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/geographic`)
      return data
    },
  })

  const complianceAreas = [
    {
      title: 'VAT & Tax Compliance',
      description: 'VAT registration, tax rates, and reporting requirements by market',
      icon: '📋',
      path: '/compliance/vat',
      color: '#4A90E2',
    },
    {
      title: 'Product Standards',
      description: 'CE marking, product safety, labeling requirements',
      icon: '✅',
      path: '/compliance/product-standards',
      color: '#50C878',
    },
    {
      title: 'Customs & Import',
      description: 'Import duties, customs procedures, documentation',
      icon: '🚢',
      path: '/compliance/customs',
      color: '#FF6B35',
    },
    {
      title: 'Data Protection',
      description: 'GDPR, data privacy regulations by region',
      icon: '🔒',
      path: '/compliance/data-protection',
      color: '#7B68EE',
    },
    {
      title: 'Consumer Rights',
      description: 'Return policies, warranty requirements, consumer protection',
      icon: '🛡️',
      path: '/compliance/consumer-rights',
      color: '#F59E0B',
    },
    {
      title: 'Business Registration',
      description: 'Local entity requirements, business licenses',
      icon: '🏢',
      path: '/compliance/business-registration',
      color: '#EF4444',
    },
  ]

  const regions = data?.regions || []

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Market Compliance Center
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            AI-powered compliance guidance for market expansion
          </p>
        </div>

        {/* Compliance Areas */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '32px' }}>
          {complianceAreas.map((area) => (
            <Card
              key={area.path}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderTop: `4px solid ${area.color}`,
              }}
              onClick={() => navigate(area.path)}
            >
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>{area.icon}</div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.textPrimary }}>
                {area.title}
              </h3>
              <p style={{ color: colors.textSecondary, fontSize: '14px', lineHeight: 1.6 }}>
                {area.description}
              </p>
            </Card>
          ))}
        </div>

        {/* Market Compliance Status */}
        <Card>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: colors.textPrimary }}>
            Compliance by Region
          </h2>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: colors.textSecondary }}>Loading regions...</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              {regions.map((region: { region: string; countries: string[]; seller_count: number }) => (
                <div
                  key={region.region}
                  style={{
                    padding: '20px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '12px',
                  }}
                >
                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', color: colors.textPrimary }}>
                    {region.region}
                  </h3>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {region.countries.slice(0, 6).map(country => (
                      <span
                        key={country}
                        onClick={() => navigate(`/compliance/${country}`)}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          fontSize: '12px',
                          color: colors.textPrimary,
                          cursor: 'pointer',
                        }}
                      >
                        {country}
                      </span>
                    ))}
                    {region.countries.length > 6 && (
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: colors.primary + '10',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: colors.primary,
                      }}>+{region.countries.length - 6} more</span>
                    )}
                  </div>
                  <p style={{ fontSize: '13px', color: colors.textSecondary }}>
                    {region.seller_count} active sellers
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </main>
    </div>
  )
}
